import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings.js'
import { propOr, propEq, prop } from 'ramda'
import { yup } from 'schemas/validation'
import { displayModuleName } from 'i18n/displayConstants'
import { Button, Row, Col, Alert, Icon } from 'components/patterns'

import XMCSvg from 'components/svg/XMC_Module_small'
import ASAMSvg from 'components/svg/ASAM_Module_small'
import RemoveIcon from 'img/remove-icon.svg'
import PendingAlertIcon from 'img/alert-pending-icon.svg'
import { XMC, ASAM, ECU, TRUCK_SUBTYPE_38N } from 'utils/constants'
import { isXMCLimitReached } from '../utils'
import {
  DrawerHeader,
  StyledDrawerHeaderButton,
  PageHeader,
  PageContent,
  StyledNotificationWrapper,
  ModuleWrapper,
  ModuleCard,
  ModuleCardHeader,
  ModuleGraphic,
  PlusSign,
  RemoveIconElement,
  RemoveModuleBtn,
  NewXMCModuleContainer,
  StatusPill,
  StyledTextArea,
  FooterStyled,
} from './AddXMC.style'

const editIconStyles = {
  width: '24px',
  height: '24px',
}

const getModuleGraphic = (moduleName) => {
  if (moduleName.includes(ASAM)) return <ASAMSvg />

  if (moduleName.includes(XMC)) return <XMCSvg />

  return null
}

const moduleRenderer = (module, idx, onAdd, onRemove, modules) => {
  /**
   * Check if a specific module/s exist before allowing remove of pending module
   * @returns {boolean} true disables remove, false enables
   */
  const isRemoveDisabled = () => {
    const moduleId = prop('id', module)

    // modules 2 and 4 are always removable
    if (['XMC2', 'XMC4'].includes(moduleId)) return false

    // module 3 is only removable if there is no 4
    // returns true if xmc4 is found. is disabled true
    if (moduleId === 'XMC3') return modules.find(propEq('XMC4', 'id'))

    // module 1 is only removable is there is no 2, 3, 4
    // returns true if any of xmc2, xmc3, xmc4 is found. is disabled true
    if (moduleId === 'XMC1') {
      return modules.find((module) =>
        ['XMC2', 'XMC3', 'XMC4'].includes(prop('id', module))
      )
    }

    return false
  }
  if (!module) {
    return (
      <ModuleCard
        key={`empty-${idx}`}
        {...{
          type: 'outlined',
          role: 'button',
          onClick: (e) => onAdd(e, idx),
        }}
      >
        <PlusSign>+</PlusSign>
        <span>
          {strings.add_xmc_btn} {idx}
        </span>
      </ModuleCard>
    )
  }
  if (module.isNewXMC) {
    return (
      <NewXMCModuleContainer key={module.id}>
        <ModuleCard>
          <ModuleCardHeader>
            <span>{module.name}</span>
            <RemoveModuleBtn
              onClick={() => onRemove(idx, true)}
              disabled={isRemoveDisabled()}
            >
              <RemoveIconElement src={RemoveIcon} />
            </RemoveModuleBtn>
          </ModuleCardHeader>
          <ModuleGraphic>
            <XMCSvg />
          </ModuleGraphic>
        </ModuleCard>
        <StatusPill src={PendingAlertIcon} />
      </NewXMCModuleContainer>
    )
  }
  return (
    <ModuleCard key={module?.uri}>
      <ModuleCardHeader>
        {displayModuleName(module.id) || module.id}
      </ModuleCardHeader>
      <ModuleGraphic>{getModuleGraphic(module.id)}</ModuleGraphic>
    </ModuleCard>
  )
}

const AddXMC = (props) => {
  const { serialNumber, closeDrawer, vehicle, addXmc } = props
  const modules = propOr([], 'modules', vehicle)
  const vehicleModules = modules.filter(propEq('type', ECU))
  const vehicleXMCs = modules.filter((item) => item.id.includes(XMC))

  const [notes, setNotes] = useState('')
  const [newXMCs, setNewXMCs] = useState([])
  const [updatedVehicleModules, setUpdatedVehicleModules] = useState([])
  const updatedVehicleXMCs = updatedVehicleModules
    .filter((item) => !item || item?.id.includes(XMC))
    .filter((item) => item)
  const updatedVehicleValidXMCs = updatedVehicleXMCs.filter((item) => item)

  const { vehicleXMCsLength, isXMCsLengthReached } = isXMCLimitReached(
    updatedVehicleValidXMCs,
    vehicle.subType,
    newXMCs
  )

  const isVehicle38N = vehicle.subType === TRUCK_SUBTYPE_38N

  const canAddNewXMCs =
    !isXMCsLengthReached && updatedVehicleValidXMCs.length > 0 && !isVehicle38N

  const handleAddNewXMCBtnClick = (_e, position) => {
    if (position) {
      setUpdatedVehicleModules((prev) => {
        let prevArr = [...prev]

        prevArr[position] = {
          id: `${XMC}${position}`,
          name: `${XMC} ${position}`,
          isNewXMC: true,
        }
        prevArr = prevArr.map((item) => ({
          ...item,
          xmcCount: vehicleXMCsLength + 1,
        }))

        if (
          (isVehicle38N && !updatedVehicleValidXMCs.length) ||
          position === 1
        ) {
          prevArr.push(null)
        }
        return prevArr
      })
    } else if (!isXMCsLengthReached) {
      setNewXMCs((prevState) => {
        const newState = [...prevState]
        const newvehicleXMCsLength =
          prevState.length === 0
            ? vehicleXMCsLength === 3
              ? 4
              : 3
            : prevState[prevState.length - 1]?.xmcCount + 1

        newState.push({
          id: `${XMC}${newvehicleXMCsLength}`,
          name: `${XMC} ${newvehicleXMCsLength}`,
          xmcCount: newvehicleXMCsLength,
        })

        return newState
      })
    }
  }

  const handleRemoveNewXMC = (idx, shouldUpdateFromVehicleModules) => {
    if (shouldUpdateFromVehicleModules) {
      setUpdatedVehicleModules((prevState) => {
        let newState = [...prevState]
        if (idx === 1) {
          newState.splice(idx, 1)
        } else {
          newState.splice(idx, 1, null)
        }
        const { length } = newState.filter((x) => x)
        newState = newState.map((item) =>
          item ? { ...item, xmcCount: length - 1 } : null
        )
        return newState
      })
    } else {
      setNewXMCs((prevState) => {
        const newState = [...prevState]
        newState.splice(idx, 1)
        return newState.map((item, index) => {
          item.id = `${XMC}${vehicleXMCsLength + index + 1}`
          return item
        })
      })
    }
  }

  const handleAddtoVehicleBtnClick = () => {
    addXmc({
      serialNumber,
      requestBody: {
        modules: newXMCs.concat(
          updatedVehicleValidXMCs
            .filter((item) => item.isNewXMC)
            .map(({ isNewXMC, xmcCount, ...item }) => item)
        ),
        notes,
      },
    })
    closeDrawer()
  }

  useEffect(() => {
    const shouldShowAddXMC = (() => {
      let count = 0
      return (idx, moduleId) => {
        if (
          idx > 0 &&
          Number(moduleId.substring(moduleId.length - 1)) !== idx + count
        ) {
          count = 1
          return true
        }
        count = 0
        return false
      }
    })()

    const arr = []
    vehicleModules.forEach((item, index) => {
      if (shouldShowAddXMC(index, item.id)) {
        arr.push(null, item)
      } else {
        arr.push(item)
      }
      if (!vehicleXMCs.length || (vehicleXMCs.length === 1 && index === 1)) {
        arr.push(null)
      }
    })
    setUpdatedVehicleModules(arr)
    // eslint-disable-next-line
  }, [])

  // what the heck.....
  const disableAddBtn = !(
    (newXMCs.length || updatedVehicleValidXMCs.length > vehicleXMCs.length) &&
    notes.length
  )

  return (
    <>
      <PageContent>
        <DrawerHeader>
          <Icon icon="edit" style={editIconStyles} fill="white" />
          {
            <span>
              <strong>{strings.add_xmc_to_vehicle}</strong> {serialNumber}
            </span>
          }
          <StyledDrawerHeaderButton
            onClick={closeDrawer}
            type="secondary"
            onColor="light"
          >
            {strings.cancel}
          </StyledDrawerHeaderButton>
        </DrawerHeader>
        <PageHeader
          backIcon={false}
          title={`${strings.xmc_modules} ${serialNumber}`}
        />
        <StyledNotificationWrapper>
          <Alert
            type="warning"
            persistent
            showIcon={false}
            message={strings.add_xmc_warning_msg}
          />
        </StyledNotificationWrapper>
        <ModuleWrapper>
          {updatedVehicleModules.map((module, idx) =>
            moduleRenderer(
              module,
              idx,
              handleAddNewXMCBtnClick,
              handleRemoveNewXMC,
              [...updatedVehicleModules, ...newXMCs].filter((module) => module)
            )
          )}
          {newXMCs.map((module, index) => (
            <NewXMCModuleContainer key={module.id}>
              <ModuleCard>
                <ModuleCardHeader>
                  <span>{module.name}</span>
                  <RemoveModuleBtn
                    onClick={() => handleRemoveNewXMC(index)}
                    disabled={index !== newXMCs.length - 1}
                  >
                    <RemoveIconElement src={RemoveIcon} />
                  </RemoveModuleBtn>
                </ModuleCardHeader>
                <ModuleGraphic>
                  <XMCSvg />
                </ModuleGraphic>
              </ModuleCard>
              <StatusPill src={PendingAlertIcon} />
            </NewXMCModuleContainer>
          ))}
          {canAddNewXMCs && (
            <ModuleCard
              type="outlined"
              role="button"
              onClick={handleAddNewXMCBtnClick}
            >
              <PlusSign>+</PlusSign>
              <span>
                {strings.add_xmc_btn}{' '}
                {updatedVehicleModules.length + newXMCs.length}
              </span>
            </ModuleCard>
          )}
        </ModuleWrapper>
        <StyledTextArea
          required
          label={strings.notes}
          name={strings.notes}
          onChange={setNotes}
          value={notes}
          placeholder={strings.notes_required}
          maxLength={1000}
          rules={yup.string().required()}
        />
      </PageContent>
      <FooterStyled>
        <Row>
          <Col span={24}>
            <Button onClick={closeDrawer} type="secondary">
              {strings.cancel}
            </Button>
            <Button
              disabled={disableAddBtn}
              onClick={handleAddtoVehicleBtnClick}
              type="primary"
            >
              {strings.add_to_vehicle_btn}
            </Button>
          </Col>
        </Row>
      </FooterStyled>
    </>
  )
}

AddXMC.propTypes = {
  vehicle: PropTypes.object,
  serialNumber: PropTypes.string,
  closeDrawer: PropTypes.func,
  addXmc: PropTypes.func,
}

export default AddXMC
